import { HalfaLaserStrip } from "@/components/HalfaLaserStrip";
import { GuideStrip } from "@/components/GuideButton";
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowRight, Type, Image as ImageIcon, QrCode, FileDown, Truck, Clock } from "lucide-react";
import { api, eur } from "@/lib/api";
import { StampSvg } from "@/components/StampSvg";
import { ProductThumb, sizeLabel } from "@/components/ProductThumb";
import { SizeText } from "@/components/RichText";
import { productPhotos } from "@/components/ProductGallery";
import { computeLayout, initialDesign } from "@/lib/layout";
import { useMeta } from "@/lib/meta";

const HERO = "https://images.pexels.com/photos/3838313/pexels-photo-3838313.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export function ProductCard({ p, i = 0 }) {
  const photo = p.kind !== "pad" && productPhotos(p)[0];
  return (
    <Link to={`/timbro/${p.slug}`} data-testid={`product-card-${p.slug}`} style={{ animationDelay: `${i * 60}ms` }}
      className="fade-up group rounded-xl border border-slate-200 bg-white overflow-hidden hover:border-brand-navy hover:shadow-[6px_6px_0_#EDD93F] hover:-translate-y-0.5 transition-[transform,border-color] duration-300">
      <div className="ink-grid h-44 flex items-center justify-center relative">
        {photo ? (
          <>
            <img src={photo} alt={p.name} loading="lazy" data-testid={`product-card-photo-${p.slug}`} className="absolute inset-0 w-full h-full object-contain p-3 bg-white transition-opacity duration-300 group-hover:opacity-0" />
            <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-300"><ProductThumb p={p} w={200} h={120} /></span>
          </>
        ) : <ProductThumb p={p} w={200} h={120} />}
        <span className="absolute top-3 left-3 z-10 font-mono text-[10px] bg-white/90 px-2 py-0.5 rounded border border-slate-200">{p.kind === "pad" ? "Cuscinetto di ricambio" : sizeLabel(p)}</span>
      </div>
      <div className="p-4 flex items-end justify-between gap-3">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{p.brand}{p.sku && <span data-testid={`product-card-sku-${p.slug}`} className="text-brand-navy">{p.brand && " · "}Cod. {p.sku}</span>}</div>
          <div className="font-display text-xl font-bold uppercase leading-tight"><SizeText text={p.name} /></div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[10px] text-slate-500">da</div>
          <div className="font-bold text-brand-navy">{eur(p.price)}</div>
        </div>
      </div>
    </Link>
  );
}

export default function Home() {
  const [products, setProducts] = useState([]);
  const meta = useMeta();
  useEffect(() => { api.get("/products", { params: { kind: "stamp" } }).then((r) => setProducts(r.data)); }, []);
  const steps = [
    [Type, "Scrivi il testo", "Righe, font, grassetto, allineamento e testo curvo per i timbri tondi."],
    [ImageIcon, "Aggiungi grafica", "Carica il tuo logo, scegli una clip art o genera un QR code."],
    [FileDown, "Anteprima PDF 1:1", "Scarica l'anteprima in scala reale e controllala stampata."],
    [Truck, "Ricevilo a casa", "Produciamo a Cesena e spediamo in tutta Italia."],
  ];
  return (
    <div>
      <HalfaLaserStrip />
      <GuideStrip />
      <section className="relative overflow-hidden border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 lg:py-24 grid lg:grid-cols-[1.1fr_1fr] gap-12 items-center">
          <div className="space-y-7 fade-up">
            <span className="inline-flex items-center gap-2 font-mono text-[11px] tracking-widest text-brand-navy border border-brand-yellow bg-brand-yellow/20 px-3 py-1 rounded-full"><Clock className="w-3 h-3" /> ORDINA ENTRO LE 12 · SPEDITO IN GIORNATA</span>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold uppercase leading-[0.92] tracking-tight">
              Il tuo timbro,<br /><span className="bg-brand-yellow px-2 box-decoration-clone">composto al millimetro.</span>
            </h1>
            <p className="text-base md:text-lg text-slate-600 max-w-xl">Editor online con anteprima in tempo reale, righe illimitate dove il modello lo consente, loghi, clip art e QR code. Vedi esattamente cosa stamperai.</p>
            <div className="flex flex-wrap gap-3">
              <Link to="/catalogo" data-testid="hero-start-btn" className="press inline-flex items-center gap-2 h-12 px-6 rounded-md bg-brand-yellow text-brand-navy font-semibold hover:bg-brand-yellowdark">Crea il tuo timbro <ArrowRight className="w-4 h-4" /></Link>
              <Link to="/timbro/trodat-printy-4912" data-testid="hero-bestseller-btn" className="press inline-flex items-center gap-2 h-12 px-6 rounded-md border border-slate-300 bg-white font-semibold hover:border-brand-navy">Trodat 4912 più venduto</Link>
            </div>
          </div>
          <div className="relative">
            <img src={HERO} alt="Timbri artigianali" className="w-full h-[420px] object-cover rounded-xl" />
            {meta && products[1] && (
              <div className="absolute -bottom-6 -left-6 bg-white rounded-lg shadow-xl p-4 border border-slate-200 rotate-[-3deg] hidden sm:block">
                <StampSvg layout={computeLayout(products[1], initialDesign(products[1]))} color="#DC2626" scale={5} realistic />
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
        <div className="grid md:grid-cols-4 gap-px bg-slate-200 rounded-xl overflow-hidden border border-slate-200">
          {steps.map(([Icon, t, d], i) => (
            <div key={t} className="bg-white p-7 space-y-3">
              <div className="flex items-center justify-between"><Icon className="w-6 h-6 text-brand-navy" /><span className="font-mono text-xs text-slate-400">0{i + 1}</span></div>
              <div className="font-display text-2xl font-bold uppercase">{t}</div>
              <p className="text-sm text-slate-600">{d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="font-mono text-[11px] tracking-widest text-slate-500">CATALOGO</div>
            <h2 className="text-4xl sm:text-5xl font-extrabold uppercase">I più scelti</h2>
          </div>
          <Link to="/catalogo" className="text-sm font-semibold text-brand-navy hover:underline flex items-center gap-1" data-testid="home-all-products-link">Tutti i timbri <ArrowRight className="w-4 h-4" /></Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {products.slice(0, 8).map((p, i) => <ProductCard key={p.id} p={p} i={i} />)}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-24">
        <div className="rounded-2xl bg-brand-navy text-white p-10 md:p-14 grid md:grid-cols-[1.4fr_1fr] gap-8 items-center">
          <div className="space-y-3">
            <QrCode className="w-8 h-8 text-brand-yellow" />
            <h2 className="text-4xl font-extrabold uppercase">Hai bisogno di aiuto con la bozza?</h2>
            <p className="text-slate-400 max-w-lg">Chiamaci o scrivici su WhatsApp: i nostri operatori di Cesena controllano ogni timbro prima della produzione.</p>
          </div>
          <div className="flex flex-col sm:flex-row md:flex-col gap-3 md:items-end">
            <a href="tel:+390547611342" className="press h-12 px-6 rounded-md bg-white text-brand-navy font-semibold flex items-center justify-center">Chiama 0547 611342</a>
            <a href="https://wa.me/390547611342" target="_blank" rel="noreferrer" className="press h-12 px-6 rounded-md bg-brand-yellow text-brand-navy font-semibold flex items-center justify-center">Scrivi su WhatsApp</a>
          </div>
        </div>
      </section>
    </div>
  );
}
